import { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { APP_PROMPT_CONTEXT_KEY } from "../components/home/AppDownloadPrompt";
import { StoreBadges } from "../components/home/StoreBadges";
import { apiUrl } from "../lib/api";

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function buildNativeJoinUrl(inviteToken: string | null, poolCode: string | null) {
  const params = new URLSearchParams();
  if (inviteToken) {
    params.set("invite", inviteToken);
  } else if (poolCode) {
    params.set("pool_code", poolCode);
  }
  const query = params.toString();
  return query ? `sportex100://join?${query}` : null;
}

export function JoinPoolPage() {
  const { poolCode: routePoolCodeParam } = useParams();
  const routePoolCode = routePoolCodeParam?.trim() ?? "";

  if (routePoolCode) {
    return <PoolInviteSharePage routePoolCode={routePoolCode} />;
  }

  return <JoinRedirectPage />;
}

function JoinRedirectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useUser();
  
  const inviteToken = searchParams.get("invite");
  const poolCode = searchParams.get("pool_code");
  const hasJoinTarget = Boolean(inviteToken || poolCode);

  useEffect(() => {
    if (!hasJoinTarget) {
      return;
    }

    const nativeJoinUrl = buildNativeJoinUrl(inviteToken, poolCode);
    if (!nativeJoinUrl || !isIOSDevice()) {
      return;
    }

    window.location.href = nativeJoinUrl;
  }, [hasJoinTarget, inviteToken, poolCode]);

  useEffect(() => {
    if (!hasJoinTarget) {
      return;
    }

    if (!isLoaded) {
      return;
    }

    if (inviteToken) {
      const startedSignedOutInvite = sessionStorage.getItem("pendingInviteStartedSignedOut");

      sessionStorage.setItem(APP_PROMPT_CONTEXT_KEY, "invite");
      sessionStorage.setItem("pendingInviteToken", inviteToken);
      if (!isSignedIn) {
        sessionStorage.setItem("pendingInviteStartedSignedOut", inviteToken);
        sessionStorage.removeItem("pendingInviteRequiresFreshAuth");
      }
      if (isSignedIn && startedSignedOutInvite !== inviteToken) {
        sessionStorage.setItem("pendingInviteRequiresFreshAuth", "1");
      }
    }

    if (poolCode) {
      sessionStorage.setItem(APP_PROMPT_CONTEXT_KEY, "pool_code");
      sessionStorage.setItem("pendingPoolCode", poolCode);
      sessionStorage.setItem("pendingPoolCodeAuthSource", "join_link");
    }
    
    navigate("/", { replace: true });
  }, [hasJoinTarget, inviteToken, isLoaded, isSignedIn, poolCode, navigate]);

  if (!hasJoinTarget) {
    return <JoinLinkFallbackPage />;
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 md:gap-6 p-6">
      <div className="rounded border border-brand-border bg-state-accent-bg-faint/60 p-6 shadow-card text-center">
        <p className="text-body text-foreground-subtle">Loading...</p>
      </div>
    </div>
  );
}

function JoinLinkFallbackPage() {
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-6 md:gap-6">
      <section className="rounded border border-brand-border bg-state-accent-bg-faint/60 p-6 text-center shadow-card">
        <p className="mb-2 text-label uppercase tracking-[0.12em] text-foreground-subtle">
          Pool invite link
        </p>
        <h1 className="mb-3 text-heading text-foreground">Open SportEx100 to join a pool</h1>
        <p className="mx-auto mb-5 max-w-lg text-body text-foreground-subtle">
          Invite links include a pool code or private invite token. If you already have a code,
          open SportEx100 and enter it from the join screen.
        </p>
        <div className="mb-5 flex justify-center">
          <StoreBadges className="justify-center" />
        </div>
        <a className="text-link font-semibold hover:text-brand-hover" href="/">
          Continue to SportEx100
        </a>
      </section>
    </div>
  );
}

type PublicPoolInvite = {
  poolCode: string;
  poolName: string;
  inviterName: string;
};

type PoolInviteSharePageProps = {
  routePoolCode: string;
};

const UNIVERSAL_JOIN_BASE_URL = "https://sportex100.com/join";

function PoolInviteSharePage({ routePoolCode }: PoolInviteSharePageProps) {
  const [pool, setPool] = useState<PublicPoolInvite | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadPool = async () => {
      setIsLoading(true);
      setIsInvalid(false);

      try {
        const response = await fetch(apiUrl(`/api/pools/public-invite/${encodeURIComponent(routePoolCode)}`));
        if (!response.ok) {
          if (isActive) {
            setPool(null);
            setIsInvalid(true);
          }
          return;
        }
        const data = (await response.json()) as PublicPoolInvite;
        if (isActive) {
          setPool(data);
        }
      } catch {
        if (isActive) {
          setPool(null);
          setIsInvalid(true);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadPool();

    return () => {
      isActive = false;
    };
  }, [routePoolCode]);

  useEffect(() => {
    if (!isCopied) return;
    const timer = window.setTimeout(() => setIsCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [isCopied]);

  const poolCode = pool?.poolCode ?? routePoolCode;
  const poolName = pool?.poolName ?? "SportEx100";
  const universalJoinUrl = `${UNIVERSAL_JOIN_BASE_URL}?pool_code=${encodeURIComponent(poolCode)}`;
  const whatsappShareText = `Join ${poolName} on SportEx100!\n\nCode: ${poolCode}\n\n${universalJoinUrl}`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(whatsappShareText)}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(universalJoinUrl);
    setIsCopied(true);
  };

  return (
    <div className="grid min-h-screen place-items-center bg-[#0a0c10] px-4 py-6 text-white">
      <section className="w-full max-w-[400px] rounded-[20px] border border-white/[0.08] bg-[#141820] px-[22px] py-6 text-center">
        <img
          src="/img/sportex100-square.png"
          alt="SportEx100"
          className="mx-auto mb-4 h-[58px] w-[58px] rounded-xl"
        />
        {isInvalid ? (
          <h1 className="m-0 text-[22px] font-extrabold leading-tight text-[#FFB020]">
            This code is no longer valid.
          </h1>
        ) : (
          <>
            <p className="mb-2 text-[11px] font-bold uppercase leading-tight tracking-[1px] text-white/25">
              BRING FRIENDS IN
            </p>
            <h1 className="mb-6 text-[26px] font-extrabold leading-[1.12] tracking-normal text-[#FFB020]">
              {isLoading ? "Loading..." : poolName}
            </h1>
            {!isLoading && pool && (
              <>
                <p className="mb-[18px] text-sm leading-normal text-white/45">
                  Share this pool with friends. They can open SportEx100 and enter the code.
                </p>
                <div className="mb-4 rounded-xl border border-[#f5a623]/20 bg-[#0a0c10] px-[18px] py-4">
                  <p className="mb-1.5 text-[10px] font-bold uppercase leading-tight tracking-[1px] text-white/25">
                    POOL CODE
                  </p>
                  <div className="overflow-wrap-anywhere font-mono text-[28px] font-extrabold leading-tight tracking-[6px] text-[#FFB020]">
                    {poolCode}
                  </div>
                </div>
                <a
                  href={whatsappShareUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex w-full items-center justify-center gap-4 rounded-xl border-0 bg-[#25D366] p-[15px] text-[16px] font-extrabold leading-none text-white"
                >
                  <span className="whatsapp-mark grid h-7 w-7 flex-none place-items-center rounded-full bg-white" aria-hidden="true">
                    <svg
                      viewBox="0 0 32 32"
                      focusable="false"
                      className="h-[18px] w-[18px] fill-[#25D366]"
                    >
                      <path
                        d="M26.576 5.363c-2.69-2.69-6.406-4.354-10.511-4.354-8.209 0-14.865 6.655-14.865 14.865 0 2.732 0.737 5.291 2.022 7.491l-0.038-0.070-2.109 7.702 7.879-2.067c2.051 1.139 4.498 1.809 7.102 1.809h0.006c8.209-0.003 14.862-6.659 14.862-14.868 0-4.103-1.662-7.817-4.349-10.507l0 0zM16.062 28.228h-0.005c-0 0-0.001 0-0.001 0-2.319 0-4.489-0.64-6.342-1.753l0.056 0.031-0.451-0.267-4.675 1.227 1.247-4.559-0.294-0.467c-1.185-1.862-1.889-4.131-1.889-6.565 0-6.822 5.531-12.353 12.353-12.353s12.353 5.531 12.353 12.353c0 6.822-5.53 12.353-12.353 12.353h-0zM22.838 18.977c-0.371-0.186-2.197-1.083-2.537-1.208-0.341-0.124-0.589-0.185-0.837 0.187-0.246 0.371-0.958 1.207-1.175 1.455-0.216 0.249-0.434 0.279-0.805 0.094-1.15-0.466-2.138-1.087-2.997-1.852l0.010 0.009c-0.799-0.74-1.484-1.587-2.037-2.521l-0.028-0.052c-0.216-0.371-0.023-0.572 0.162-0.757 0.167-0.166 0.372-0.434 0.557-0.65 0.146-0.179 0.271-0.384 0.366-0.604l0.006-0.017c0.043-0.087 0.068-0.188 0.068-0.296 0-0.131-0.037-0.253-0.101-0.357l0.002 0.003c-0.094-0.186-0.836-2.014-1.145-2.758-0.302-0.724-0.609-0.625-0.836-0.637-0.216-0.010-0.464-0.012-0.712-0.012-0.395 0.010-0.746 0.188-0.988 0.463l-0.001 0.002c-0.802 0.761-1.3 1.834-1.3 3.023 0 0.026 0 0.053 0.001 0.079l-0-0.004c0.131 1.467 0.681 2.784 1.527 3.857l-0.012-0.015c1.604 2.379 3.742 4.282 6.251 5.564l0.094 0.043c0.548 0.248 1.25 0.513 1.968 0.74l0.149 0.041c0.442 0.14 0.951 0.221 1.479 0.221 0.303 0 0.601-0.027 0.889-0.078l-0.031 0.004c1.069-0.223 1.956-0.868 2.497-1.749l0.009-0.017c0.165-0.366 0.261-0.793 0.261-1.242 0-0.185-0.016-0.366-0.047-0.542l0.003 0.019c-0.092-0.155-0.34-0.247-0.712-0.434z"
                      />
                    </svg>
                  </span>
                  <span>Share on WhatsApp</span>
                </a>
                <button
                  type="button"
                  onClick={handleCopy}
                  className={`mt-2.5 w-full cursor-pointer rounded-xl border p-[15px] text-[15px] font-extrabold leading-none ${
                    isCopied
                      ? "border-[#22c55e]/30 bg-[#22c55e]/10 text-[#22c55e]"
                      : "border-[#f5a623]/30 bg-[#f5a623]/10 text-[#FFB020]"
                  }`}
                >
                  {isCopied ? "✓ Link copied!" : "Copy invite link"}
                </button>
                <div className="my-6 grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-[11px] font-bold uppercase tracking-[1px] text-white/25 before:border-t before:border-white/[0.06] after:border-t after:border-white/[0.06]">
                  Need the app?
                </div>
                <StoreBadges className="justify-center" />
              </>
            )}
        </>
        )}
        <footer className="mt-5 text-[11px] text-white/20">© 2026 SportEx100</footer>
      </section>
    </div>
  );
}
